import { useState, useEffect, useCallback } from "react";
import { ResponsiveModal, ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ShoppingCart, ChevronLeft, ChevronRight, Scan } from "lucide-react";
import { cn } from "@/lib/utils";
import { getDemoProfile } from "@/utils/demoProfile";

interface StickerPack {
  id: string;
  name: string;
  images: string[];
  price: number;
  details: string[];
}

const stickerPacks: StickerPack[] = [
  {
    id: "shinchan",
    name: "Shinchan QR Sticker Pack",
    images: [
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png",
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627375/Site_Posts_1_gypuwl.png"
    ],
    price: 189,
    details: [
      "Pack of 3 high-quality QR stickers",
      "Waterproof and weather-resistant",
      "Easy to apply on any surface",
      "Instant connection when scanned",
      "Perfect for metro cards, laptops, and more"
    ]
  },
  {
    id: "green-alien",
    name: "Green Alien Sticker Pack",
    images: [
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627361/Site_Posts_7_xzu6fl.png",
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_9_twcxeh.png"
    ],
    price: 189,
    details: [
      "Pack of 3 high-quality QR stickers",
      "Waterproof and weather-resistant",
      "Easy to apply on any surface",
      "Instant connection when scanned",
      "Perfect for metro cards, laptops, and more"
    ]
  },
  {
    id: "red-alien",
    name: "Red Alien Sticker Pack",
    images: [
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627368/Site_Posts_2_ast368.png",
      "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_8_dgqyme.png"
    ],
    price: 189,
    details: [
      "Pack of 3 high-quality QR stickers",
      "Waterproof and weather-resistant",
      "Easy to apply on any surface",
      "Instant connection when scanned",
      "Perfect for metro cards, laptops, and more"
    ]
  }
];

interface StickerPacksDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  openDemoProfile?: (profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    x_username: string | null;
    bio: string | null;
    metro_card_number: string | null;
    created_at: string;
    updated_at: string;
    show_email: boolean;
    show_mobile: boolean;
    metefy_token: string | null;
    photo_url?: string | null;
    mask_bio?: boolean;
    social_sms?: string | null;
    social_call?: string | null;
    social_telegram?: string | null;
    social_whatsapp?: string | null;
    use_social_icons?: boolean;
  }) => void;
}

export function StickerPacksDrawer({ isOpen, setIsOpen, openDemoProfile }: StickerPacksDrawerProps) {
  const [selectedPack, setSelectedPack] = useState<string>("shinchan");
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const selectedPackData = stickerPacks.find(pack => pack.id === selectedPack);

  const handleWhatsAppOrder = () => {
    if (!selectedPackData) return;
    
    const message = encodeURIComponent(
      `Hey! I am interested to buy ${selectedPackData.name} with pricing of ₹${selectedPackData.price} and pack of three stickers.`
    );
    const whatsappUrl = `https://wa.me/918307562996?text=${message}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePackSwitch = (packId: string) => {
    setSelectedPack(packId);
    setCurrentImageIndex(0);
    setShowDetails(false);
  };

  const handleTryDemo = () => {
    if (!openDemoProfile) return;
    openDemoProfile(getDemoProfile());
  };

  const nextImage = () => {
    if (!selectedPackData) return;
    setCurrentImageIndex((prev) => (prev + 1) % selectedPackData.images.length);
  };

  const prevImage = () => {
    if (!selectedPackData) return;
    setCurrentImageIndex((prev) => (prev - 1 + selectedPackData.images.length) % selectedPackData.images.length);
  };

  // Reset image index when pack changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedPack]);

  // Wrapper function for closing drawer
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Add history state when drawer opens, handle back button
  useEffect(() => {
    if (isOpen) {
      // Push a history state so back button can close the drawer
      window.history.pushState({ stickerDrawerOpen: true }, '');
      
      const onPopState = () => {
        if (isOpen) {
          // Call handleClose - ResponsiveModal/VaulDrawer will handle the animation
          handleClose();
        }
      };
      
      window.addEventListener('popstate', onPopState);
      return () => {
        window.removeEventListener('popstate', onPopState);
      };
    }
  }, [isOpen, handleClose]);

  if (!selectedPackData) return null;

  return (
    <ResponsiveModal 
      open={isOpen} 
      setOpen={setIsOpen} 
      direction="bottom" 
      classname="max-w-md border-2 border-primary"
      drawerClassName="border-2 border-primary"
    >
      <ResponsiveModalContent
        className="w-full max-h-[90vh] overflow-y-auto bg-background text-foreground rounded-2xl sm:max-w-md p-0 pb-6 focus:outline-none focus-visible:outline-none"
        titleId="sticker-packs-title"
      >
        <div className="space-y-3 relative p-3 sm:p-6 bg-card">
          {/* Current Sticker Pack */}
          <div className="rounded-xl bg-card transition-all w-full">
            {/* Pack Name */}
            <div className="mb-4 px-4 pt-4">
              <h3 className="text-foreground font-semibold text-lg text-center">{selectedPackData.name}</h3>
            </div>

            {/* Large Image Display - One at a time */}
            <div className="mb-4 px-4">
              <div className="relative flex items-center justify-center w-full">
                {/* Previous Image Button */}
                {selectedPackData.images.length > 1 && (
                  <button
                    onClick={prevImage}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 border border-border"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}

                {/* Image Container - Same as AboutHeroSection */}
                <div className="h-[300px] overflow-hidden relative">
                  <div className="w-[300px] h-[300px] relative mx-auto">
                    {selectedPackData.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${selectedPackData.name} ${index + 1}`}
                        className={cn(
                          "w-[300px] h-[300px] object-contain rounded-lg transition-opacity duration-300 select-none absolute top-0 left-0",
                          index === currentImageIndex ? "opacity-100" : "opacity-0"
                        )}
                        width="300"
                        height="300"
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        draggable={false}
                        fetchpriority={index === 0 ? "high" : "auto"}
                      />
                    ))}
                  </div>
                </div>

                {/* Next Image Button */}
                {selectedPackData.images.length > 1 && (
                  <button
                    onClick={nextImage}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 border border-border"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Dots Indicator */}
              {selectedPackData.images.length > 1 && (
                <div className="flex justify-center items-center mt-4 gap-2">
                  {selectedPackData.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={cn(
                        "w-3 h-3 rounded-full transition-all duration-300",
                        index === currentImageIndex
                          ? "bg-primary scale-110"
                          : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                      )}
                      aria-label={`Go to sticker ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Sticker Pack Selection Tabs - Below Images */}
            <div className="flex gap-2 mb-4 pb-2 px-4 min-w-0">
              {stickerPacks.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => handlePackSwitch(pack.id)}
                  className={cn(
                    "flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap transition-all",
                    selectedPack === pack.id
                      ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
                      : "bg-transparent border border-primary text-foreground"
                  )}
                >
                  <span className="truncate block">{pack.name.replace(" Sticker Pack", "")}</span>
                </button>
              ))}
            </div>

            {/* Price - Inline with Clickable Dropdown */}
            <div className="mb-3 px-4 pb-4">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-center gap-2 p-2 rounded-lg"
              >
                <p className="text-lg font-bold text-foreground">
                  ₹{selectedPackData.price}
                </p>
                <p className="text-sm text-foreground/80">• Pack of three stickers</p>
                {showDetails ? (
                  <ChevronUp className="w-4 h-4 text-foreground/70 ml-auto" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-foreground/70 ml-auto" />
                )}
              </button>
              {showDetails && (
                <div className="mt-2 space-y-3">
                  <div className="pl-2 space-y-1">
                    {selectedPackData.details.map((detail, index) => (
                      <p key={index} className="text-sm text-foreground/80">
                        • {detail}
                      </p>
                    ))}
                  </div>
                  {/* Try Demo Button inside expandable section */}
                  {openDemoProfile && (
                    <Button
                      onClick={handleTryDemo}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-semibold"
                    >
                      <Scan className="w-5 h-5 mr-2" />
                      Try Demo
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Order on WhatsApp Button */}
          <div className="px-4">
            <Button
              onClick={handleWhatsAppOrder}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg font-semibold"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Order on WhatsApp
            </Button>
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
